import {
  ComponentProps,
  PropsWithChildren,
  ReactNode,
  forwardRef,
  useEffect,
  useRef,
} from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

import { VariantProps, cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

import { X } from '../assets';
import { cn, useViewportStore } from '../lib';
import { AnimatedWrapper } from './animated-wrapper';
import { Button } from './button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './dialog';
import { Drawer, DrawerContent, DrawerTrigger } from './drawer';
import { Heading } from './heading';
import { IModalProps, ModalTemplate } from './templates';
import { Typography } from './typography';

const variants = cva('p-0 border-none overflow-hidden w-full', {
  variants: {
    size: {
      lg: 'max-w-[630px]',
      md: 'max-w-[342px]',
      auto: 'w-auto',
      fullscreen: 'max-w-screen',
    },
    height: {
      default: 'h-auto',
      fixed: 'h-[600px]',
      fullscreen: 'h-screen',
    },
  },
  defaultVariants: {
    size: 'md',
    height: 'default',
  },
});

export interface DashboardDialogProps
  extends ComponentProps<typeof Dialog>,
    PropsWithChildren,
    VariantProps<typeof variants> {
  title?: string;
  titleProps?: ComponentProps<typeof Heading>;
  modalProps?: Omit<IModalProps, 'id'>;
  subtitle?: string;
  subtitleProps?: ComponentProps<typeof Typography>;
  headerProps?: ComponentProps<typeof DialogHeader>;
  hideCloseButton?: boolean;
  hideOverlay?: boolean;
  isFullscreen?: boolean;
  serviceButton?: {
    icon: ReactNode;
    onClick?: VoidFunction;
    isMobile?: boolean;
  };
  handleOnly?: boolean;
  disableOutsideClick?: boolean;
  content: ReactNode;
  disableAutoFocus?: boolean;
  contentClassName?: string;
  header?: ReactNode;
  closeButtonProps?: ComponentProps<typeof Button>;
  animationProps?: ComponentProps<typeof AnimatedWrapper>;
  triggerProps?: ComponentProps<typeof DialogTrigger>;
  scrollToTopId?: string;
  onClose?: () => void;
}

export const SmallCloseButton = ({ className }: { className?: string }) => {
  return (
    <DialogPrimitive.Close asChild>
      <Button variant="dark-secondary" size="rounded-sm" className={className}>
        <X className="text-base-500" />
      </Button>
    </DialogPrimitive.Close>
  );
};
const CloseButton = ({
  className,
  ...props
}: ComponentProps<typeof Button>) => {
  const button = (
    <Button
      className={cn(
        'absolute right-1 top-1 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-base-300 p-0',
        className,
      )}
      variant="dark-secondary"
      {...props}
    >
      <X className="min-h-5 min-w-5" />
      <span className="sr-only">Close</span>
    </Button>
  );
  if (props.onClick) {
    return button;
  }
  return <DialogPrimitive.Close asChild>{button}</DialogPrimitive.Close>;
};

export const DashboardDialog = forwardRef<HTMLDivElement, DashboardDialogProps>(
  (
    {
      children,
      title,
      modalProps = {},
      hideCloseButton,
      onClose,
      serviceButton,
      content,
      titleProps,
      disableOutsideClick,
      disableAutoFocus,
      handleOnly,
      subtitle,
      subtitleProps,
      hideOverlay,
      size,
      headerProps,
      contentClassName,
      header,
      closeButtonProps,
      animationProps,
      triggerProps,
      height,
      scrollToTopId,
      isFullscreen,
      ...props
    },
    ref,
  ) => {
    // use modal system
    const modalRef = useRef<HTMLDivElement>(null);
    const isMobile = useViewportStore((state) => state.isMobile);
    const drawerContentRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (!modalRef.current || !scrollToTopId) return;
      modalRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }, [scrollToTopId]);

    if (isMobile)
      return (
        <Drawer
          {...props}
          dismissible={!disableOutsideClick}
          handleOnly={handleOnly}
        >
          {children && (
            <DrawerTrigger {...triggerProps}>{children}</DrawerTrigger>
          )}

          <DrawerContent
            disableAutoFocus={disableAutoFocus}
            className="!max-w-auto bottom-1 overflow-hidden rounded-none !bg-[transparent] px-1"
            overlayClassName="backdrop-blur-[50px] bg-modal"
            closeButton={null}
            ref={drawerContentRef}
            noOverlay={hideOverlay}
          >
            <ModalTemplate
              ref={modalRef}
              id="dashboard-dialog"
              title={title}
              isHasAnimationLayout={false}
              description={subtitle}
              classNames={{
                wrapper: twMerge(
                  '!rounded-[32px]',
                  modalProps?.classNames?.wrapper,
                ),
                ...(modalProps?.classNames ?? {}),
              }}
              className={twMerge('p-1', modalProps?.className)}
              isHasCloseBtn={!hideCloseButton}
              onClose={onClose}
              HeaderComponent={header}
              LeftBtn={
                serviceButton && {
                  isIcon: true,
                  Component: serviceButton.icon,
                  onClick: serviceButton.onClick,
                }
              }
              {...modalProps}
              isFullHeight={isFullscreen}
              isFullWidth={isFullscreen}
            >
              {content}
            </ModalTemplate>
          </DrawerContent>
        </Drawer>
      );
    return (
      <Dialog {...props}>
        {children && (
          <DialogTrigger {...triggerProps}>{children}</DialogTrigger>
        )}

        <DialogContent
          disableAutoFocus={disableAutoFocus}
          overlayClassName="backdrop-blur-[50px] bg-modal"
          className="block w-max !max-w-max overflow-hidden rounded-none border-none !bg-[transparent] !p-0 shadow-none"
          disableOutsideClick={disableOutsideClick}
          noOverlay={hideOverlay}
          closeButton={null}
        >
          <VisuallyHidden>
            <DialogTitle>modal</DialogTitle>
          </VisuallyHidden>
          <ModalTemplate
            ref={ref}
            id="dashboard-dialog"
            title={title}
            isHasCloseBtn={!hideCloseButton}
            description={subtitle}
            HeaderComponent={header}
            isFullHeight={isFullscreen}
            isFullWidth={isFullscreen}
            onClose={onClose}
            classNames={{
              wrapper: twMerge(
                '!rounded-[32px]',
                modalProps?.classNames?.wrapper,
              ),
              title: twMerge(!!title && 'mt-1', modalProps?.classNames?.title),
              ...(modalProps?.classNames ?? {}),
            }}
            className={twMerge(
              'overflow-x-hidden !rounded-[32px] p-1',
              modalProps?.className,
            )}
            isDashboard
            LeftBtn={
              serviceButton && !serviceButton?.isMobile
                ? {
                    isIcon: true,
                    Component: serviceButton.icon,
                    onClick: serviceButton.onClick,
                  }
                : undefined
            }
            {...modalProps}
          >
            {content}
          </ModalTemplate>
        </DialogContent>
      </Dialog>
    );
  },
);
